import Alert, { AlertProps } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { isConnectionFailure } from './alliance/connection-monitor';

interface ErrorResponse {
  status: string | null;
  errors: { field: string | null; type: string | null; msg: string | null }[];
}

/**
 * Shows a failed mutation's error.
 *
 * The API's `msg` is always shown when there is one: the messages are
 * written for end users, and hiding them made agents retry blindly. `field`
 * and `type` stay behind `displayMore` (admins). Without a message it falls
 * back to a generic line that never says "refresh the page".
 *
 * @param displayMore - Also show each error's field and type
 */
export function DisplayMutationError(props: {
  isError: boolean;
  error: Error | null;
  onClose: AlertProps['onClose'];
  displayMore?: boolean;
  alertTitle?: string;
}) {
  if (!props.isError) {
    return null;
  }

  // @ts-ignore
  const data: ErrorResponse | undefined = props.error?.response?.data;
  const errors = (data?.errors || []).filter(
    (item) => item.msg || (props.displayMore && (item.field || item.type)),
  );

  const fallback = isConnectionFailure(props.error)
    ? 'We couldn’t reach the server. Check your connection and try again.'
    : 'Something went wrong. Please try again.';

  return (
    <Alert severity="error" onClose={props.onClose}>
      <AlertTitle>{props.alertTitle || 'Error'}</AlertTitle>

      {errors.length ? (
        <Stack spacing={1}>
          {errors.map((item, index) => {
            const { field, type, msg } = item;
            return (
              <Stack
                key={index}
                spacing={1}
                direction="row"
                alignItems="center"
              >
                {props.displayMore && field ? (
                  <Box component="strong">{field}</Box>
                ) : null}
                {props.displayMore && type ? (
                  <Box component="strong">{type}</Box>
                ) : null}
                {msg ? <Box>{msg}</Box> : null}
              </Stack>
            );
          })}
        </Stack>
      ) : (
        fallback
      )}
    </Alert>
  );
}
